"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Pause, Eye } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function HangedManCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-teal-500/10 border border-teal-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-teal-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-teal-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-teal-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-teal-500">/</span>
            <span className="text-sm">The Hanged Man</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">The Hanged Man</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-teal-900/20 to-cyan-900/20 rounded-xl p-8 mb-8 border border-teal-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-hanged-man.svg"
                  alt="The Hanged Man Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XII</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">The Hanged Man</h1>
                  <p className="text-xl text-teal-400">Asılan Adam / Fedakarlık</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Fedakarlık, sabır ve farklı perspektifin kartı. Gönüllü duraksama,
                yeniden değerlendirme ve içsel aydınlanma için bekleyiş dönemini temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-sm">Fedakarlık</span>
                <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-sm">Sabır</span>
                <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-sm">Perspektif</span>
                <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-sm">Bekleyiş</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gönüllü Fedakarlık</h3>
                  <p className="text-slate-400 text-sm">Daha büyük bir amacı için kişisel isteklerden vazgeçme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yeni Perspektif</h3>
                  <p className="text-slate-400 text-sm">Farklı açıdan bakma, eski kalıpları sorgulama.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sabırlı Bekleyiş</h3>
                  <p className="text-slate-400 text-sm">Doğru zamanı bekleme, acele etmeme bilgeliği.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İçsel Aydınlanma</h3>
                  <p className="text-slate-400 text-sm">Durgunluk içinde bilgelik kazanma, ruhsal büyüme.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Pause className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gereksiz Fedakarlık</h3>
                  <p className="text-slate-400 text-sm">Anlamlı olmayan vazgeçişler, kurban rolünde takılma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kararsızlık</h3>
                  <p className="text-slate-400 text-sm">Eyleme geçememe, belirsizlik içinde kalma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Direniş</h3>
                  <p className="text-slate-400 text-sm">Değişime karşı koyma, eski düzende ısrar etme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İsteksiz Durgunluk</h3>
                  <p className="text-slate-400 text-sm">Zoraki bekleme, pasif direnç, motivasyon eksikliği.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🎋</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">T Şeklindeki Ağaç</h3>
              <p className="text-slate-400 text-sm">Ruhsal büyüme, yaşam ağacı ve fedakarlığın kutsal boyutu.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Eye className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Halo</h3>
              <p className="text-slate-400 text-sm">Ruhsal aydınlanma, kutsal bilgelik ve içsel ışık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👖</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kırmızı Pantolon</h3>
              <p className="text-slate-400 text-sm">Tutku, yaşam enerjisi ve fiziksel dünyaya bağlılık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👕</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Mavi Gömlek</h3>
              <p className="text-slate-400 text-sm">Sakinlik, ruhsal barış ve yüksek farkındalık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-brown-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🪢</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İp</h3>
              <p className="text-slate-400 text-sm">Bağlılık, karma bağları ve seçilmiş kısıtlamalar.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🍃</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yeşil Yapraklar</h3>
              <p className="text-slate-400 text-sm">Yeni büyüme, yeniden doğuş ve sürekli yaşam.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlişki için fedakarlık yapma</li>
                <li>• Sabırlı bekleyiş dönemi</li>
                <li>• Farklı perspektiften değerlendirme</li>
                <li>• Unutkanlığa bırakma</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Kariyerde mola verme</li>
                <li>• Farklı yaklaşımlar deneme</li>
                <li>• Sabırlı bekleme dönemi</li>
                <li>• Yeni fırsatları değerlendirme</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İçsel yolculuk yapma</li>
                <li>• Meditasyon ve kontemplasyon</li>
                <li>• Perspektif değiştirme</li>
                <li>• Ruhsal olgunlaşma</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Pause className="h-5 w-5 text-blue-400" />
                Sağlık ve Dinlenme
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Dinlenme ve yenilenme</li>
                <li>• Stresle başa çıkma</li>
                <li>• Sakinlik bulma</li>
                <li>• Mental detoks yapma</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/justice"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Justice
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/death"
            className="inline-flex items-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
          >
            Death
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}